// Regras puras do mapa da frota e do veículo no mapa da rota (sem React, sem API).
import type { FleetLiveVehicle, TrackPoint } from '../../types/vehicleTracker';

/** Intervalo do polling das telas de mapa (o aparelho envia a cada ~60 s). */
export const FLEET_POLL_MS = 10_000;

/** Até 5 min sem pacote o veículo conta como "ao vivo". */
export const LIVE_MAX_SECONDS = 300;
/** Abaixo disso o veículo está parado (ruído do GPS parado fica em 1–4 km/h). */
export const MOVING_MIN_KMH = 5;

export type VehicleMapState = 'moving' | 'stopped' | 'silent' | 'offline' | 'no_position';

/**
 * Velocidade atual: a da última posição com GPS fixado, só se ela tem até 5 min.
 * O heartbeat (LK) e o pacote sem fix renovam a comunicação mas não a
 * velocidade: sem esse corte, o veículo parado na base aparecia "em movimento"
 * horas com a velocidade do último fix.
 */
export function currentSpeedKmh(v: Pick<FleetLiveVehicle, 'speed_kmh' | 'last_position_seconds_ago'>): number {
  const ago = v.last_position_seconds_ago;
  if (ago == null || ago > LIVE_MAX_SECONDS) return 0;
  return v.speed_kmh ?? 0;
}

/**
 * Situação do veículo no mapa:
 * - em movimento / parado: comunicou há até 5 min (pela velocidade atual, ver currentSpeedKmh);
 * - em silêncio: até 24 h sem pacote (o TK915 dorme parado);
 * - sem comunicação: mais de 24 h ou nunca;
 * - sem posição: nunca teve GPS fixado (não aparece no mapa).
 */
export function vehicleMapState(
  v: Pick<FleetLiveVehicle, 'latitude' | 'longitude' | 'last_seen_seconds_ago' | 'last_position_seconds_ago' | 'speed_kmh'>,
): VehicleMapState {
  if (v.latitude == null || v.longitude == null) return 'no_position';
  const ago = v.last_seen_seconds_ago;
  if (ago == null || ago > 86400) return 'offline';
  if (ago > LIVE_MAX_SECONDS) return 'silent';
  return currentSpeedKmh(v) >= MOVING_MIN_KMH ? 'moving' : 'stopped';
}

const STATE_INFO: Record<VehicleMapState, { label: string; color: string; variant: 'success' | 'info' | 'warning' | 'danger' | 'neutral' }> = {
  moving: { label: 'Em movimento', color: '#16A34A', variant: 'success' },
  stopped: { label: 'Parado', color: '#2563EB', variant: 'info' },
  silent: { label: 'Em silêncio', color: '#D97706', variant: 'warning' },
  offline: { label: 'Sem comunicação', color: '#64748B', variant: 'danger' },
  no_position: { label: 'Sem posição', color: '#94A3B8', variant: 'neutral' },
};

export function vehicleStateInfo(state: VehicleMapState) {
  return STATE_INFO[state];
}

/** "agora", "45 min", "1 h 20 min", "2 d 3 h" a partir de segundos. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return '—';
  if (seconds < 60) return 'menos de 1 min';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const restH = hours % 24;
  return restH ? `${days} d ${restH} h` : `${days} d`;
}

/** "19:33" de "2026-10-12T19:33:10" (horário de Brasília, sem passar por Date). */
export function formatClock(iso: string | null | undefined): string {
  const m = iso?.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '—';
}

/** "12/10 19:33" de "2026-10-12T19:33:10". */
export function formatDayClock(iso: string | null | undefined): string {
  const m = iso?.match(/^\d{4}-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return m ? `${m[2]}/${m[1]} ${m[3]}:${m[4]}` : '—';
}

/** Data de hoje (relógio local da máquina, que é Brasília) em ISO aaaa-mm-dd. */
export function todayISO(now: Date = new Date()): string {
  const y = now.getFullYear();
  const mo = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

const EARTH_RADIUS_KM = 6371;

/** Distância em km entre dois pontos (haversine). */
export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Km percorridos somando os trechos entre posições. Trechos menores que 20 m
 * são ignorados: é o "passeio" do GPS com o veículo parado.
 */
export function trackDistanceKm(points: Pick<TrackPoint, 'latitude' | 'longitude'>[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const d = haversineKm(points[i - 1].latitude, points[i - 1].longitude, points[i].latitude, points[i].longitude);
    if (d >= 0.02) total += d;
  }
  return total;
}

/** "12,3 km" */
export function formatKm(km: number): string {
  return `${km.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

/** Contagem por situação, para o resumo do topo do mapa. */
export function fleetSummary(vehicles: FleetLiveVehicle[]): Record<VehicleMapState, number> & { onRoute: number } {
  const out = { moving: 0, stopped: 0, silent: 0, offline: 0, no_position: 0, onRoute: 0 };
  for (const v of vehicles) {
    out[vehicleMapState(v)]++;
    if (v.active_route) out.onRoute++;
  }
  return out;
}

/** "ABC1D23 — Fiorino" (só o que existir). */
export function vehicleName(v: Pick<FleetLiveVehicle, 'license_plate' | 'description' | 'vehicle_id'>): string {
  const parts = [v.license_plate, v.description].filter((p) => p && p.trim());
  return parts.length ? parts.join(' — ') : `Veículo #${v.vehicle_id}`;
}

export type FleetGroup = 'all' | 'delivery' | 'executive';

/** Filtro do mapa: todos, só veículos de entrega ou só os da diretoria. */
export function filterByGroup<T extends Pick<FleetLiveVehicle, 'is_executive'>>(vehicles: T[], group: FleetGroup): T[] {
  if (group === 'delivery') return vehicles.filter((v) => !v.is_executive);
  if (group === 'executive') return vehicles.filter((v) => v.is_executive);
  return vehicles;
}

/** Soma dias a uma data ISO aaaa-mm-dd (sem passar por fuso). */
export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return todayISO(dt);
}

/** Quantidade de dias do período (inclusivo): 2026-10-10..2026-10-12 = 3. */
export function periodDays(from: string, to: string): number {
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2, d2] = to.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000) + 1;
}

/** Trajeto: até 7 dias; lista de paradas: até 31 dias (limites da API). */
export const TRACK_MAX_DAYS = 7;
export const STOPS_MAX_DAYS = 31;

export type PeriodPreset = 'today' | 'yesterday' | 'last7';

export function periodPreset(preset: PeriodPreset, today: string): { from: string; to: string } {
  if (preset === 'yesterday') {
    const y = addDaysISO(today, -1);
    return { from: y, to: y };
  }
  if (preset === 'last7') return { from: addDaysISO(today, -6), to: today };
  return { from: today, to: today };
}
